<script setup lang="ts">
defineProps({
  size: {
    type: String as () => 'lg' | 'xl',
    default: 'xl',
  },
  placeholder: {
    type: String,
    default: 'Rechercher des rabais, produits ou recettes...',
  },
  maxHeight: {
    type: String,
    default: 'max-h-124 md:max-h-110',
  },
  resultPadding: {
    type: String,
    default: 'px-4 py-3',
  },
  imageSize: {
    type: String,
    default: 'w-16 h-16',
  },
  iconSize: {
    type: String,
    default: 'w-8 h-8',
  },
  fontSize: {
    type: String,
    default: 'text-sm',
  },
})

const userStore = useUserStore()
const config = useRuntimeConfig()
const router = useRouter()
const localePath = useLocalePath()
const modalStore = useModalStore()

// Search state
const searchTerm = ref('')
const searchResults = ref<any>({ flyers: [], flyer_items: [], recipes: [] })
const isSearching = ref(false)
const showSearchResults = ref(false)
const debouncedSearchTerm = ref('')
const debounceTimeout = ref<NodeJS.Timeout | null>(null)
const currentAbortController = ref<AbortController | null>(null)
const searchInputRef = ref<HTMLElement | null>(null)
const searchFieldRef = ref<HTMLInputElement | null>(null)
const hasOpenDropdown = ref(false)
const enterPressed = ref(false)

// Debounce search term
watch(searchTerm, (newTerm) => {
  // Reset enterPressed when user types
  enterPressed.value = false

  if (newTerm.length >= 3) {
    if (debounceTimeout.value) {
      clearTimeout(debounceTimeout.value)
    }
    debounceTimeout.value = setTimeout(() => {
      debouncedSearchTerm.value = newTerm
    }, 350)
  }
  else {
    searchResults.value = { flyers: [], flyer_items: [], recipes: [] }
    showSearchResults.value = false
  }
})

// Watch debounced term and perform search
watch(debouncedSearchTerm, async (newTerm) => {
  if (newTerm && newTerm.length >= 3) {
    isSearching.value = true
    // Only show search results if Enter hasn't been pressed
    if (!enterPressed.value) {
      showSearchResults.value = true
    }
    await performSearch(newTerm)
  }
})

// Perform search
async function performSearch(term: string) {
  if (!term || term.length < 3)
    return

  try {
    // Cancel previous request
    if (currentAbortController.value) {
      currentAbortController.value.abort()
    }

    currentAbortController.value = new AbortController()

    // Prepare query parameters
    const queryParams: any = {
      q: term,
      t: 'sugar',
    }

    // Add user stores if user is connected and has stores
    if (userStore.user && userStore.user.stores && userStore.user.stores.length > 0) {
      queryParams.stores = userStore.user.stores.join(',')
    }

    const response = await $fetch(`${config.public.apiUrl}/search/global`, {
      method: 'GET',
      query: queryParams,
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
      signal: currentAbortController.value.signal,
    })

    searchResults.value = formatSearchResults(response)
  }
  catch (error: any) {
    if (error.name !== 'AbortError') {
      console.error('Search error:', error)
    }
  }
  finally {
    isSearching.value = false
  }
}

// Format search results
function formatSearchResults(response: any) {
  const results: any = {
    flyers: [],
    flyer_items: [],
    recipes: [],
  }

  // Handle flyers
  if (response.results?.flyers?.hits && response.results.flyers.hits.length > 0) {
    results.flyers = response.results.flyers.hits.slice(0, 3)
  }

  // Handle flyer items
  if (response.results?.flyer_items?.hits && response.results.flyer_items.hits.length > 0) {
    results.flyer_items = response.results.flyer_items.hits.slice(0, 5)
  }

  // Handle recipes
  if (response.results?.recipes?.hits && response.results.recipes.hits.length > 0) {
    results.recipes = response.results.recipes.hits.slice(0, 5)
  }

  return results
}

// Calculate flyer status
function getFlyerStatus(flyer: any) {
  const now = new Date()
  const startDate = new Date(flyer.start_date)
  const endDate = new Date(flyer.end_date)

  if (now < startDate) {
    const diffTime = startDate.getTime() - now.getTime()
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60))
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffHours < 24) {
      return `Débute dans ${diffHours} heure${diffHours > 1 ? 's' : ''}`
    }
    else {
      return `Débute dans ${diffDays} jour${diffDays > 1 ? 's' : ''}`
    }
  }
  else if (now <= endDate) {
    const diffTime = endDate.getTime() - now.getTime()
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60))
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffHours < 24) {
      return `Se termine dans ${diffHours} heure${diffHours > 1 ? 's' : ''}`
    }
    else {
      return `Se termine dans ${diffDays} jour${diffDays > 1 ? 's' : ''}`
    }
  }
  else {
    const diffTime = now.getTime() - endDate.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return `Terminé depuis ${diffDays} jour${diffDays > 1 ? 's' : ''}`
  }
}

// Get flyer status color class
function getFlyerStatusColor(flyer: any) {
  const now = new Date()
  const startDate = new Date(flyer.start_date)
  const endDate = new Date(flyer.end_date)

  if (now >= startDate && now <= endDate) {
    return 'text-green-600'
  }
  else {
    return 'text-yellow-600'
  }
}

// Check if there are any results
const hasResults = computed(() => {
  return searchResults.value.flyers?.length > 0
    || searchResults.value.flyer_items?.length > 0
    || searchResults.value.recipes?.length > 0
})

// Handle result click
function handleFlyerClick(flyer: any) {
  showSearchResults.value = false
  // Ne pas effacer searchTerm pour qu'il reste visible
  router.push(localePath({
    name: 'FlyerPage',
    params: { id: flyer.id, slug: flyer.flyer_slug },
  }))
}

function handleFlyerItemClick(item: any) {
  // Keep search results open when opening modal so user can click other items after closing modal
  // Ne pas effacer searchTerm pour qu'il reste visible
  modalStore.openItemModal(item.id)
}

function handleRecipeClick(recipe: any) {
  showSearchResults.value = false
  // Ne pas effacer searchTerm pour qu'il reste visible
  router.push(localePath({
    name: 'RecipePage',
    params: { id: recipe.id, slug: recipe.slug },
  }))
}

// Handle Enter key
function handleSearchEnter() {
  // Mark that Enter has been pressed to prevent search results from opening
  enterPressed.value = true

  // Always close search results and blur the input when Enter is pressed
  showSearchResults.value = false

  if (import.meta.client && document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }

  if (searchTerm.value && searchTerm.value.length >= 3) {
    const targetPage = !userStore.user ? 'RecipesSearchResultsPage' : 'FlyerItemsSearchResultsPage'

    // Si on reste sur la même page, mettre à jour seulement la query

    router.push(localePath({
      name: targetPage,
      query: { q: searchTerm.value },
    }))

    // Ne pas effacer le searchTerm pour qu'il reste après la redirection
  }
}

// Handle focus - reopen results if there are any
function handleSearchFocus() {
  // Reset enterPressed when user focuses back on the input
  enterPressed.value = false

  if (searchTerm.value && searchTerm.value.length >= 3 && hasResults.value) {
    showSearchResults.value = true
  }
}

// Handle dropdown open/close
function handleDropdownOpen() {
  hasOpenDropdown.value = true
}

function handleDropdownClose() {
  hasOpenDropdown.value = false
}

// Close search results when clicking outside
onMounted(() => {
  if (import.meta.client) {
    // Use mousedown to capture state before click handlers
    document.addEventListener('mousedown', (e: Event) => {
      const target = e.target as HTMLElement

      // Check if clicking inside a dropdown menu (it's rendered in a portal/teleport)
      const isInsideDropdown = target.closest('[role="menu"]') !== null

      // Check if clicking inside a modal/overlay (they're rendered in portals)
      const isInsideModal = target.closest('[role="dialog"]') !== null || target.closest('.fixed.inset-0') !== null

      // Capture the dropdown state BEFORE the click is processed
      const wasDropdownOpen = hasOpenDropdown.value

      // Use setTimeout to check after click handlers have run
      setTimeout(() => {
        // Don't close if a dropdown was open, if clicking inside one, or if clicking inside/closing a modal
        if (searchInputRef.value && !searchInputRef.value.contains(target) && !wasDropdownOpen && !isInsideDropdown && !isInsideModal) {
          showSearchResults.value = false
        }
      }, 50)
    })
  }
})
</script>

<template>
  <div ref="searchInputRef" class="w-full">
    <UInput
      ref="searchFieldRef"
      v-model="searchTerm"
      icon="i-ic-sharp-search"
      :size="size"
      :placeholder="placeholder"
      :loading="isSearching"
      class="w-full xl:max-w-120"
      :ui="{ trailing: 'pe-1', base: 'rounded-full h-9 xl:h-11' }"
      @keydown.enter="handleSearchEnter"
      @focus="handleSearchFocus"
    >
      <template v-if="searchTerm?.length" #trailing>
        <UButton
          color="neutral"
          variant="ghost"
          size="lg"
          icon="i-mdi-close"
          aria-label="Clear input"
          @click="searchTerm = ''"
        />
      </template>
    </UInput>

    <!-- Search Results Dropdown -->
    <div
      v-if="showSearchResults && !isSearching"
      class="absolute top-full left-0 right-0 mt-0 xl:ml-2 bg-white border border-gray-200 rounded-b-lg shadow-xl overflow-y-auto z-50 overflow-x-hidden max-h-96 md:max-h-110 xl:max-w-200"
    >
      <!-- Results -->
      <div class="divide-y divide-gray-100">
        <!-- Circulaires Section -->
        <div v-if="searchResults.flyers?.length > 0" class="py-2">
          <div class="px-4 py-1 text-sm font-semibold text-gray-600 uppercase tracking-wide">
            Circulaires
          </div>
          <button
            v-for="flyer in searchResults.flyers"
            :key="flyer.id"
            class="w-full text-left hover:bg-gray-50 transition-colors flex items-center gap-3 px-4 py-1"
            @click="handleFlyerClick(flyer)"
          >
            <UAvatar
              :ui="{ root: 'rounded flex-shrink-0' }"
              :src="flyer.flyer_thumb"
              icon="i-lucide-image"
              size="xl"
            />
            <div class="flex flex-col justify-between min-w-0 flex-1">
              <div class="text-left">
                <img
                  :src="`${config.public.backendCdnUrl}/u/stores/${flyer.store_slug}.png`"
                  class="h-5"
                  :alt="flyer.store_name"
                >
              </div>
              <div class="text-xs text-left" :class="getFlyerStatusColor(flyer)">
                {{ getFlyerStatus(flyer) }}
              </div>
            </div>
          </button>
          <ULink
            :to="localePath({ name: 'FlyersPage' })"
            class="flex items-center justify-center gap-1.5 py-2 px-4 mx-3 my-2 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors group"
            @click="showSearchResults = false"
          >
            <span class="text-sm font-medium text-gray-700 group-hover:text-gray-900">Toutes les circulaires</span>
            <UIcon name="i-lucide-arrow-right" class="size-4 text-gray-500 group-hover:text-gray-700 group-hover:translate-x-0.5 transition-transform" />
          </ULink>
        </div>

        <!-- Rabais Section -->
        <div class="py-2">
          <div class="px-4 py-1 text-sm font-semibold text-gray-600 uppercase tracking-wide">
            Rabais des circulaires
          </div>
          <template v-if="!userStore.user">
            <div class="px-3 py-2 text-center">
              <p class="text-sm text-gray-600 mb-3">
                Connectez-vous pour chercher dans les rabais des circulaires.
              </p>
              <div class="flex gap-2 justify-center">
                <UButton
                  size="sm"
                  color="primary"
                  label="Se connecter"
                  @click="showSearchResults = false;modalStore.openLoginModal()"
                />
                <UButton
                  :to="localePath({ name: 'RegisterPage' })"
                  size="sm"
                  color="secondary"
                  variant="outline"
                  label="Créer un compte"
                  @click="showSearchResults = false"
                />
              </div>
            </div>
          </template>
          <template v-else-if="searchResults.flyer_items?.length > 0">
            <div
              v-for="item in searchResults.flyer_items"
              :key="item.id"
              class="w-full hover:bg-gray-50 transition-colors flex items-center gap-3 px-4 py-1"
            >
              <button
                class="flex items-center gap-3 flex-1 min-w-0 text-left"
                @click="handleFlyerItemClick(item)"
              >
                <UAvatar
                  :ui="{ root: 'rounded border-1 border-gray-300 flex-shrink-0' }"
                  :src="item.cdn_thumbnail_url"
                  icon="i-lucide-image"
                  size="xl"
                />
                <div class="flex flex-col min-w-0 flex-1">
                  <div class="text-poivre text-left first-letter-uppercase truncate text-sm md:text-base">
                    <span v-html="item._formatted?.item_label || item.item_label" />
                    <span v-if="item.item_description" class="text-tomate-400 font-bold mx-0.5">&middot;</span>
                    <span v-if="item.item_description" class="text-poivre-400">{{ item.item_description }}</span>
                  </div>
                  <div class="text-left flex items-center space-x-1.5">
                    <img
                      :src="`${config.public.backendCdnUrl}/u/stores/${item.store_slug}.png`"
                      class="h-3.5"
                      :alt="item.store_name"
                    >
                    <span
                      v-if="item.ing_score > 0 && userStore.user && userStore.user.extra"
                      class="text-xs text-white rounded-full px-1"
                      :style="{ backgroundColor: item.colors?.star_color }"
                    >
                      {{ item.ing_score }}
                    </span>
                    <span class="text-sm text-tomate-600 font-semibold">{{ item.price_description }}</span>
                  </div>
                </div>
              </button>
              <div class="flex-shrink-0" @click.stop>
                <ShoppingListMiniButton :flyer-item="item" @dropdown-open="handleDropdownOpen" @dropdown-close="handleDropdownClose" />
              </div>
            </div>
            <ULink
              :to="localePath({ name: 'FlyerItemsSearchResultsPage', query: { q: debouncedSearchTerm } })"
              class="flex items-center justify-center gap-1.5 py-2 px-4 mx-3 my-2 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors group"
              @click="showSearchResults = false"
            >
              <span class="text-sm font-medium text-gray-700 group-hover:text-gray-900">Tous les résultats</span>
              <UIcon name="i-lucide-arrow-right" class="size-4 text-gray-500 group-hover:text-gray-700 group-hover:translate-x-0.5 transition-transform" />
            </ULink>
          </template>
          <div v-else class="px-4 py-3 text-sm text-gray-500 italic">
            Aucun résultat
          </div>
        </div>

        <!-- Recettes Section -->
        <div class="py-2">
          <div class="px-4 py-1 text-sm font-semibold text-gray-600 uppercase tracking-wide">
            Recettes
          </div>
          <template v-if="searchResults.recipes?.length > 0">
            <button
              v-for="recipe in searchResults.recipes"
              :key="recipe.id"
              class="w-full text-left hover:bg-gray-50 transition-colors flex items-center gap-3 px-4 py-1"
              @click="handleRecipeClick(recipe)"
            >
              <UAvatar
                :ui="{ root: 'rounded flex-shrink-0' }"
                :src="recipe.thumbnailUrlFull"
                icon="i-lucide-image"
                size="lg"
              />
              <div class="flex flex-col min-w-0 flex-1">
                <div class="text-left truncate">
                  <span class="text-poivre" v-html="recipe._formatted?.title || recipe.title" />
                </div>
              </div>
            </button>
            <ULink
              :to="localePath({ name: 'RecipesSearchResultsPage', query: { q: debouncedSearchTerm } })"
              class="flex items-center justify-center gap-1.5 py-2 px-4 mx-3 my-2 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors group"
              @click="showSearchResults = false"
            >
              <span class="text-sm font-medium text-gray-700 group-hover:text-gray-900">Tous les résultats</span>
              <UIcon name="i-lucide-arrow-right" class="size-4 text-gray-500 group-hover:text-gray-700 group-hover:translate-x-0.5 transition-transform" />
            </ULink>
          </template>
          <div v-else class="px-4 py-3 text-sm text-gray-500 italic">
            Aucun résultat
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.first-letter-uppercase::first-letter {
  text-transform: uppercase;
}

/* Style pour les balises <em> dans les résultats de recherche */
:deep(em) {
  text-decoration: none;
  text-decoration-color: #152b68;
  text-decoration-thickness: 1px;
  background-color: #f7f6d5;
  border-radius: 0.25rem;
}

/* Scrollbar personnalisée pour les résultats de recherche */
:deep(.absolute.top-full) {
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
  scrollbar-gutter: stable;
}

:deep(.absolute.top-full)::-webkit-scrollbar {
  width: 6px;
}

:deep(.absolute.top-full)::-webkit-scrollbar-track {
  background: transparent;
  margin: 8px 0;
}

:deep(.absolute.top-full)::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 3px;
}

:deep(.absolute.top-full)::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}
</style>
